<template>
    <div class="w-full h-full flex flex-col p-2 pt-4">
        <div class="flex items-center">
            <transaction-filter enable-date-filter @update="onSearchUpdated" />
            <div class="flex min-w-fit gap-2">
                <UButton
                    size="sm"
                    variant="outline"
                    color="neutral"
                    @click="exportCSV"
                >
                    Eksport CSV
                </UButton>
                <UButton
                    size="sm"
                    variant="outline"
                    color="neutral"
                    @click="showTriBalReportExportsDialog"
                >
                    Eksport...
                </UButton>
            </div>
        </div>
        <loading-bar :style="{ visibility: loading ? 'visible' : 'hidden' }" data-cy="loading-indicator" />
        <div class="flex justify-center h-full">
            <ag-grid-vue
                :column-defs="columns"
                :row-data="ledgerAccounts"
                :grid-options="gridOptions"
                :pinned-bottom-row-data="pinnedBottomSumRow"
                class="w-full h-full max-w-screen-2xl"
                :theme="agTheme"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef} from 'vue'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {Ledger} from '@/types/reports/Ledger'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {TrialBalanceTableRow} from '@/types/reports/TrialBalanceTableRow'
import {AgGridVue} from 'ag-grid-vue3'
import {formatCurrency} from '@/services/formatter.service'
import LoadingBar from '@/components/LoadingBar.vue'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import {createTrialBalanceCSVExport} from '@/services/reports/summaryReport.service'
import {download} from '@/services/infrastructure/storage.service'
import {noop} from 'lodash'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {agTheme} from '@/agUtils/ag-theme.service'
import {GetContextMenuItemsParams, GridApi, GridOptions, GridReadyEvent, RowClassParams} from 'ag-grid-community'
import {getLedgerByDate, sumLedgerRows} from '@/genleg/ledger.service'
import TribalFirstCellRenderer from '@/tribal/TribalFirstCellRenderer.vue'
import {globalDefaultColDef} from '@/agUtils'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'

const { onEvent, onEvents, cleanUp } = useEmitter()
const gridApi = shallowRef<GridApi>()

const ledger = ref<Ledger | undefined>(undefined)
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')
const searchParams = ref<TransactionSearchParams>(defaultSearchParams())
const loading = ref(false)

const onSearchUpdated = (updatedSearchParams: TransactionSearchParams) => {
    searchParams.value = updatedSearchParams
    fetchLedger(updatedSearchParams)
}

const clear = () => {
    ledger.value = undefined
}

const fetchLedger = async function (searchParams: TransactionSearchParams) {
    loading.value = true
    ledger.value = await getLedgerByDate(searchParams)
    loading.value = false
}

const ledgerAccounts = computed(() => {
    if (!ledger.value) return []
    return ledger.value.ledgerAccounts
})

const blankPinnedBottomRow = {
    rowIsPinned: true,
    incomingBalance: 0,
    movement: 0,
    outgoingBalance: 0
}

const pinnedBottomSumRow = computed(() => {
    if (!ledgerAccounts.value || ledgerAccounts.value.length === 0) return [blankPinnedBottomRow]

    const totalIncomingBalance = sumLedgerRows(ledgerAccounts.value, 'incomingBalance')
    const totalMovement = sumLedgerRows(ledgerAccounts.value, 'movement')
    const totalOutgoingBalance = sumLedgerRows(ledgerAccounts.value, 'outgoingBalance')

    return [
        {
            rowIsPinned: true,
            incomingBalance: Math.abs(totalIncomingBalance),
            movement: Math.abs(totalMovement),
            outgoingBalance: Math.abs(totalOutgoingBalance),
        },
    ]
})

const exportCSV = async () => {
    if (searchParams.value.toDate && searchParams.value.fromDate) {
        const trialBalanceCSV = await createTrialBalanceCSVExport(searchParams.value.fromDate, searchParams.value.toDate, searchParams.value.department, searchParams.value.project)
        if (trialBalanceCSV) {
            download(trialBalanceCSV).then(noop)
        }
    }
}

const showTriBalReportExportsDialog = () => {
    if(!searchParams.value.fromDate || !searchParams.value.toDate) {
        console.error('From date and to date must be set before exporting the general ledger report.')
        return
    }
    showExportsDialog(
        ReportType.TRIBAL,
        {
            fromDate: searchParams.value.fromDate,
            toDate: searchParams.value.toDate,
        }
    )
}

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the ledger page without selected org. Should not be possible, this is a bug!')
    }
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION], () => {
        fetchLedger(searchParams.value)
    })
    onEvent(ORG_SELECTED, () => {
        clear()
        fetchLedger(searchParams.value)
    })
})

onUnmounted(cleanUp)

type AgCellParam = {
    value?: any,
    data: TrialBalanceTableRow;
};


const currencyCellRenderer = (param: AgCellParam) => formatCurrency(param.value, 2)

const gridOptions: GridOptions = {
    animateRows: true,
    enableCellTextSelection: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => (loading.value ? 'Laster....' : 'Ingen data for perioden'),
    },
    onGridReady: ({ api }: GridReadyEvent) => {
        gridApi.value = api
        api.applyColumnState({
            state: [{ colId: 'accountNo', sort: 'asc' }],
        })
    },
    getRowClass: (params: RowClassParams) => {
        if (params.node.rowPinned === 'bottom') {
            return 'text-lg'
        }
    },
    suppressMoveWhenRowDragging: true,
    getRowId: (params: AgCellParam) => params.data?.accountNo || '',
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true,
    },
    context: {
        panelId,
        panelPathSegment,
        searchParams
    },
    popupParent: document.body,
    getContextMenuItems: (params: GetContextMenuItemsParams) => {
        const items = (params.defaultItems || []).filter(item => item !== 'export')
        return [
            ...items,
            {
                name: 'Eksporter...',
                icon: '<span class="ag-icon ag-icon-save" />',
                action: showTriBalReportExportsDialog
            }
        ]
    }
}

const columns = computed(() => [
    {
        headerName: 'Konto',
        field: 'accountNo',
        flex: 1,
        cellRenderer: TribalFirstCellRenderer,
        contextMenuItems: []
    },
    {
        headerName: 'Inngående saldo',
        field: 'incomingBalance',
        width: 140,
        headerClass: 'header-right',
        cellStyle: { 'text-align': 'right', overflow: 'visible' },
        cellRenderer: currencyCellRenderer,
    },
    {
        headerName: 'Endring',
        field: 'movement',
        width: 120,
        headerClass: 'header-right',
        cellStyle: { 'text-align': 'right',  overflow: 'visible'},
        cellRenderer: currencyCellRenderer,
    },
    {
        headerName: 'Utgående saldo',
        field: 'outgoingBalance',
        width: 140,
        headerClass: 'header-right',
        cellStyle: { 'text-align': 'right',  overflow: 'visible'},
        cellRenderer: currencyCellRenderer,
    },
])
</script>

<style></style>
